import { Controller } from "@hotwired/stimulus"

/**
 * 在庫管理表用のStimulusコントローラー
 */
export default class extends Controller {
  static targets = ['branchSelect', 'datePicker']
  static values = {
    branchId: String,
    openType: String,
    carryForwardId: String,
    previousCarryForwardId: String,
    nextCarryForwardId: String
  }


  connect() {
    this._beforePrint = this.#splitTablesForPrint.bind(this)
    this._afterPrint = this.#restoreTablesAfterPrint.bind(this)
    window.addEventListener('beforeprint', this._beforePrint)
    window.addEventListener('afterprint', this._afterPrint)
  }

  disconnect() {
    window.removeEventListener('beforeprint', this._beforePrint)
    window.removeEventListener('afterprint', this._afterPrint)
  }

  // print.tailwind.css の値と合わせること
  // A4横: 210mm - 上マージン5mm - 下マージン15mm = 190mm = 19.0cm
  static PAGE_HEIGHT_CM = 19.0
  static THEAD_HEIGHT_CM = 3.8  // thead td height
  static ROW_HEIGHT_CM = 0.5    // tbody tr height

  /**
   * 印刷前: ページに収まる行数でサブテーブルに分割する
   * 行高・ヘッダ高をCMで固定しているため、スクリーン測定不要
   */
  #splitTablesForPrint() {
    const printArea = document.getElementById('print-area')
    if (!printArea) return

    const rowsPerPage = Math.floor(
      (this.constructor.PAGE_HEIGHT_CM - this.constructor.THEAD_HEIGHT_CM) / this.constructor.ROW_HEIGHT_CM
    )

    printArea.querySelectorAll('table').forEach(table => {
      const thead = table.querySelector('thead')
      const tbody = table.querySelector('tbody')
      if (!thead || !tbody) return

      const rows = Array.from(tbody.querySelectorAll(':scope > tr'))
      if (rows.length <= rowsPerPage) {
        table.setAttribute('data-split-table', '')
        return
      }

      table.setAttribute('data-original-table', '')
      table.style.display = 'none'

      let insertAfter = table
      for (let i = 0; i < rows.length; i += rowsPerPage) {
        const pageRows = rows.slice(i, i + rowsPerPage)
        const subTable = table.cloneNode(false)
        subTable.setAttribute('data-split-table', '')
        subTable.setAttribute('data-created-for-print', '')  // 動的生成のマーク
        subTable.removeAttribute('data-original-table')
        subTable.style.display = ''

        const newThead = thead.cloneNode(true)
        const newTbody = document.createElement('tbody')
        newTbody.className = tbody.className
        pageRows.forEach(row => newTbody.appendChild(row.cloneNode(true)))

        subTable.appendChild(newThead)
        subTable.appendChild(newTbody)
        insertAfter.insertAdjacentElement('afterend', subTable)
        insertAfter = subTable
      }
    })
  }

  /**
   * 印刷後: 分割テーブルを削除し元テーブルを復元する
   */
  #restoreTablesAfterPrint() {
    // 動的に生成したサブテーブルだけ削除
    document.querySelectorAll('[data-created-for-print]').forEach(el => el.remove())
    // ページ区切りのためだけにマークしたオリジナルテーブルは属性を外すだけ
    document.querySelectorAll('[data-split-table]').forEach(el => el.removeAttribute('data-split-table'))
    // 行分割で非表示にしたオリジナルテーブルを復元
    document.querySelectorAll('[data-original-table]').forEach(el => {
      el.removeAttribute('data-original-table')
      el.style.display = ''
    })
  }

  /**
   * センターで検索
   */
  searchByBranch() {
    this.#navigateToStockManagementSheets({ carry_forward_id: '0' })
  }

  /**
   * 最新分を表示する
   */
  searchLatest() {
    this.#navigateToStockManagementSheets({ carry_forward_id: '0' })
  }

  /**
   * datepickerを開く
   */
  openDatePicker() {
    this.datePickerTarget.focus()
    this.datePickerTarget.showPicker()
  }

  /**
   * カレンダーの日付で検索
   */
  searchByDate() {
    this.#navigateToStockManagementSheets({
      carry_forward_id: this.carryForwardIdValue,
      carry_forward_date: this.datePickerTarget.value
    })
  }

  /**
   * 1つ前の繰越分を表示
   */
  searchPrevious() {
    this.#navigateToStockManagementSheets({ carry_forward_id: this.previousCarryForwardIdValue })
  }

  /**
   * 1つ後の繰越分を表示
   */
  searchNext() {
    this.#navigateToStockManagementSheets({ carry_forward_id: this.nextCarryForwardIdValue })
  }

  /**
   * 在庫管理表へ遷移
   * @param {Object} additionalParams - 追加パラメータ
   */
  #navigateToStockManagementSheets(additionalParams = {}) {
    const params = new URLSearchParams({
      open_type: this.openTypeValue,
      branch_id: this.hasBranchSelectTarget ? this.branchSelectTarget.value : this.branchIdValue,
      ...additionalParams
    })
    Turbo.visit(`/stock_management_sheets?${params}`, { frame: 'stock_management_sheet_frame' })
  }
}
